import { useContent } from 'contexts/Content/Content';
import { useSounds } from 'contexts/Sounds/Sounds';
import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const PageContext = createContext<PageState>({} as PageState);

export const usePage = () => useContext(PageContext);

export function withPage(WrappedComponent: any) {
    return function PageWrappedComponent(props: any) {
        return <PageContext.Consumer>{state => <WrappedComponent {...props} page={state} />}</PageContext.Consumer>;
    };
}

export function PageProvider({ children }: PageProviderProps) {
    const [interacted, setInteracted] = useState(false);
    const [lesson, setLesson] = useState<string | null>(null);
    const [loaded, setLoaded] = useState<string | null>(null);
    const [loading, setLoading] = useState<string | null>(null);
    const content = useContent();
    const router = useRouter();
    const sounds = useSounds();

    const routerPath = useMemo(() => {
        return router.asPath.split(/[?#]/)[0];
    }, [router.asPath]);

    useEffect(() => {
        if (router.isReady && loaded !== routerPath) {
            // Get interacted and loaded
            if (loading !== routerPath) {
                setInteracted(false);
            }

            setLoaded(routerPath);
        }
    }, [loaded, loading, routerPath, router.isReady]);

    useEffect(() => {
        // Get lesson
        const newLesson = loaded?.match(/lessons\/(.*)/)?.[1] || null;
        setLesson(newLesson);

        setLoading(null);
    }, [loaded]);

    useEffect(() => {
        // Play lesson sound
        const lessonContent = (lesson && content.lessons[lesson]) || null;
        if (!loading && lessonContent?.sound) {
            sounds.play(lessonContent.sound);
        }
    }, [loading, content, lesson, sounds]);

    function show(lesson: PageLesson, replace = false) {
        // Check previous
        if (lesson === 'previous') {
            router.back();
            return;
        }

        // Set interacted and loading
        const lessonPath = `/lessons/${lesson}`;
        if (lessonPath !== loading && lessonPath !== routerPath) {
            setInteracted(true);
            setLoading(lessonPath);
            if (replace) {
                router.replace(lessonPath);
            } else {
                router.push(lessonPath);
            }
        }
    }

    const state = {
        interacted,
        lesson,
        lessonParent: lesson ? parseInt(lesson.charAt(0)) : 0,
        show,
    };

    return <PageContext.Provider value={state}>{!!loaded && children}</PageContext.Provider>;
}
